import React, { useState, useEffect, useRef } from 'react';
import './AIAssistant.css';

const AIAssistant = ({ userEmail, userRole }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0) {
      addMessage(
        `Hello! I'm your Zakim Insurance Portal AI Assistant. I can help you with:\n\n` +
        `• Creating new policies\n` +
        `• Finding policy information\n` +
        `• Processing claims\n` +
        `• Managing endorsements & cancellations\n` +
        `• Viewing reports and statistics\n` +
        `• Managing customers and products\n\n` +
        `How can I assist you today?`,
        'bot'
      );
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = (text, sender = 'user') => {
    setMessages((prev) => [...prev, { text, sender, timestamp: new Date() }]);
  };

  const handleSend = async () => {
    if (!inputText.trim()) return;

    const userMessage = inputText.trim();
    addMessage(userMessage, 'user');
    setInputText('');
    setIsTyping(true);

    try {
      const response = await processUserQuery(userMessage);
      setTimeout(() => {
        addMessage(response, 'bot');
        setIsTyping(false);
      }, 500);
    } catch (error) {
      setTimeout(() => {
        addMessage('Sorry, I encountered an error processing your request. Please try again.', 'bot');
        setIsTyping(false);
      }, 500);
    }
  };

  const processUserQuery = async (query) => {
    const lowerQuery = query.toLowerCase();

    // Policy-related queries
    if (lowerQuery.includes('new polic') || lowerQuery.includes('create polic')) {
      return await handleNewPolicyQuery(query);
    }
    
    if (lowerQuery.includes('pending') && lowerQuery.includes('polic')) {
      return await handlePendingPoliciesQuery();
    }

    if (lowerQuery.includes('expired') && lowerQuery.includes('polic')) {
      return await handleExpiredPoliciesQuery();
    }

    if (lowerQuery.includes('find polic') || lowerQuery.includes('search polic')) {
      return await handlePolicySearchQuery(query);
    }

    // Claims-related queries
    if (lowerQuery.includes('claim')) {
      return await handleClaimsQuery(query);
    }

    // Customer-related queries
    if (lowerQuery.includes('customer') || lowerQuery.includes('client')) {
      return await handleCustomerQuery(query);
    }

    // Product-related queries
    if (lowerQuery.includes('product')) {
      return await handleProductQuery(query);
    }

    // Endorsement queries
    if (lowerQuery.includes('endorsement')) {
      return await handleEndorsementQuery(query);
    }

    // Cancellation queries
    if (lowerQuery.includes('cancellation')) {
      return await handleCancellationQuery(query);
    }

    // Report queries
    if (lowerQuery.includes('report') || lowerQuery.includes('statistic')) {
      return await handleReportQuery(query);
    }

    // Dashboard/Summary queries
    if (lowerQuery.includes('dashboard') || lowerQuery.includes('summary') || lowerQuery.includes('overview')) {
      return await handleDashboardQuery();
    }

    // How-to questions
    if (lowerQuery.includes('how to') || lowerQuery.includes('how do i')) {
      return handleHowToQuery(query);
    }

    // Default response
    return `I understand you're asking about "${query}". Could you please be more specific? I can help you with:

• Policy management (create, search, approve, extend)
• Claims processing
• Customer information
• Products and rates
• Endorsements and cancellations
• Reports and analytics

Try asking something like "Show me pending policies" or "How do I create a new policy?"`;
  };

  const handleNewPolicyQuery = async (query) => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/policies/status/2');
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const count = data.data.length;
        const recent = data.data.slice(0, 3);
        
        let message = `📋 You have ${count} new ${count === 1 ? 'policy' : 'policies'} awaiting approval.\n\n`;
        message += `Recent submissions:\n`;
        
        recent.forEach((policy, idx) => {
          message += `\n${idx + 1}. Policy #${policy.PolicyNumber}\n`;
          message += `   Customer: ${policy.CustomerName || 'N/A'}\n`;
          message += `   Product: ${policy.ProductName || 'N/A'}\n`;
          message += `   Premium: KES ${Number(policy.TotalPremium || 0).toLocaleString()}\n`;
        });

        if (count > 3) {
          message += `\n...and ${count - 3} more.`;
        }

        return message;
      } else {
        return '✅ No new policies pending approval at the moment.';
      }
    } catch (error) {
      return '❌ Unable to fetch new policies. Please check your connection.';
    }
  };

  const handlePendingPoliciesQuery = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/policies/status/3');
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const count = data.data.length;
        return `⏳ There are ${count} ${count === 1 ? 'policy' : 'policies'} pending approval from management.`;
      } else {
        return '✅ No policies pending management approval.';
      }
    } catch (error) {
      return '❌ Unable to fetch pending policies.';
    }
  };

  const handleExpiredPoliciesQuery = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/policies/status/5');
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const count = data.data.length;
        return `⚠️ There are ${count} expired ${count === 1 ? 'policy' : 'policies'}. Consider reaching out to customers for renewals.`;
      } else {
        return '✅ No expired policies found.';
      }
    } catch (error) {
      return '❌ Unable to fetch expired policies.';
    }
  };

  const handlePolicySearchQuery = async (query) => {
    // Extract potential policy number or customer name from query
    const words = query.split(' ');
    const searchTerm = words[words.length - 1];

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/policies');
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const filtered = data.data.filter(p => 
          p.PolicyNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filtered.length > 0) {
          let message = `🔍 Found ${filtered.length} matching ${filtered.length === 1 ? 'policy' : 'policies'}:\n\n`;
          
          filtered.slice(0, 5).forEach((policy, idx) => {
            message += `${idx + 1}. ${policy.PolicyNumber} - ${policy.CustomerName}\n`;
            message += `   Status: ${getStatusText(policy.Status)}\n`;
            message += `   Premium: KES ${Number(policy.TotalPremium || 0).toLocaleString()}\n\n`;
          });

          return message;
        } else {
          return `No policies found matching "${searchTerm}". Try searching with a different term.`;
        }
      }
      
      return 'No policies found in the system.';
    } catch (error) {
      return '❌ Unable to search policies.';
    }
  };

  const handleClaimsQuery = async (query) => {
    const lowerQuery = query.toLowerCase();

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/claims');
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const claims = data.data;

        if (lowerQuery.includes('new')) {
          const newClaims = claims.filter(c => c.ClaimStatus === 'Pending');
          return `📝 You have ${newClaims.length} new ${newClaims.length === 1 ? 'claim' : 'claims'} pending review.`;
        } else if (lowerQuery.includes('ongoing')) {
          const ongoingClaims = claims.filter(c => c.ClaimStatus === 'Ongoing');
          return `🔄 There are ${ongoingClaims.length} ${ongoingClaims.length === 1 ? 'claim' : 'claims'} currently being processed.`;
        } else if (lowerQuery.includes('settled')) {
          const settledClaims = claims.filter(c => c.ClaimStatus === 'Settled');
          return `✅ ${settledClaims.length} ${settledClaims.length === 1 ? 'claim has' : 'claims have'} been settled.`;
        } else {
          const pending = claims.filter(c => c.ClaimStatus === 'Pending').length;
          const ongoing = claims.filter(c => c.ClaimStatus === 'Ongoing').length;
          const settled = claims.filter(c => c.ClaimStatus === 'Settled').length;
          
          return `📊 Claims Summary:\n\n• Pending: ${pending}\n• Ongoing: ${ongoing}\n• Settled: ${settled}\n• Total: ${claims.length}`;
        }
      }
      
      return 'No claims found in the system.';
    } catch (error) {
      return '❌ Unable to fetch claims information.';
    }
  };

  const handleCustomerQuery = async (query) => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/customers');
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const count = data.data.length;
        
        if (query.toLowerCase().includes('how many') || query.toLowerCase().includes('count')) {
          return `👥 You have ${count} registered ${count === 1 ? 'customer' : 'customers'} in the system.`;
        }

        const recent = data.data.slice(0, 3);
        let message = `👥 Customer Database (${count} total):\n\n`;
        message += `Recent customers:\n`;
        
        recent.forEach((customer, idx) => {
          message += `\n${idx + 1}. ${customer.CustomerName || customer.Fullname}\n`;
          message += `   Email: ${customer.Email}\n`;
          message += `   Phone: ${customer.Phone || 'N/A'}\n`;
        });

        return message;
      }
      
      return 'No customers found in the database.';
    } catch (error) {
      return '❌ Unable to fetch customer information.';
    }
  };

  const handleProductQuery = async (query) => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/products');
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const products = data.data;
        const activeProducts = products.filter(p => p.Status === 1);

        let message = `🛡️ Insurance Products:\n\n`;
        message += `Active Products: ${activeProducts.length}\n\n`;

        activeProducts.slice(0, 5).forEach((product, idx) => {
          message += `${idx + 1}. ${product.ProductName}\n`;
          message += `   Type: ${product.ProductType || 'N/A'}\n`;
          message += `   Rate: ${product.ProductRate}%\n\n`;
        });

        if (activeProducts.length > 5) {
          message += `...and ${activeProducts.length - 5} more products.`;
        }

        return message;
      }
      
      return 'No products found in the system.';
    } catch (error) {
      return '❌ Unable to fetch product information.';
    }
  };

  const handleEndorsementQuery = async (query) => {
    const lowerQuery = query.toLowerCase();

    try {
      const endpoint = lowerQuery.includes('pending') 
        ? 'https://main.victriedemos.com:3017/api/endorsements/pending'
        : 'https://main.victriedemos.com:3017/api/endorsements/approved';
      
      const response = await fetch(endpoint);
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const count = data.data.length;
        const type = lowerQuery.includes('pending') ? 'pending' : 'approved';
        
        return `📋 You have ${count} ${type} ${count === 1 ? 'endorsement' : 'endorsements'}.`;
      }
      
      return lowerQuery.includes('pending') 
        ? '✅ No pending endorsements.'
        : '✅ No approved endorsements.';
    } catch (error) {
      return '❌ Unable to fetch endorsement information.';
    }
  };

  const handleCancellationQuery = async (query) => {
    const lowerQuery = query.toLowerCase();

    try {
      const endpoint = lowerQuery.includes('pending')
        ? 'https://main.victriedemos.com:3017/api/cancellations/pending'
        : 'https://main.victriedemos.com:3017/api/cancellations/approved';
      
      const response = await fetch(endpoint);
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const count = data.data.length;
        const type = lowerQuery.includes('pending') ? 'pending' : 'approved';
        
        return `🚫 You have ${count} ${type} ${count === 1 ? 'cancellation' : 'cancellations'}.`;
      }
      
      return lowerQuery.includes('pending')
        ? '✅ No pending cancellations.'
        : '✅ No approved cancellations.';
    } catch (error) {
      return '❌ Unable to fetch cancellation information.';
    }
  };

  const handleReportQuery = async (query) => {
    try {
      let message = '📊 Available Reports:\n\n';

      // Fetch monthly production
      const productionResponse = await fetch('https://main.victriedemos.com:3017/api/reports/monthly-production');
      const productionData = await productionResponse.json();

      if (productionData.success && productionData.data.length > 0) {
        const totalPremium = productionData.data.reduce((sum, item) => sum + Number(item.TotalPremium || 0), 0);
        message += `💰 Monthly Production: KES ${totalPremium.toLocaleString()}\n`;
        message += `📈 Policies Created: ${productionData.data.length}\n\n`;
      }

      // Fetch renewals
      const renewalsResponse = await fetch('https://main.victriedemos.com:3017/api/reports/upcoming-renewals');
      const renewalsData = await renewalsResponse.json();

      if (renewalsData.success && renewalsData.data.length > 0) {
        message += `🔄 Upcoming Renewals: ${renewalsData.data.length}\n\n`;
      }

      message += `\nYou can access detailed reports from the Reports menu.`;
      
      return message;
    } catch (error) {
      return '❌ Unable to fetch reports. Please try again.';
    }
  };

  const handleDashboardQuery = async () => {
    try {
      const [policiesRes, claimsRes, customersRes] = await Promise.all([
        fetch('https://main.victriedemos.com:3017/api/policies'),
        fetch('https://main.victriedemos.com:3017/api/claims'),
        fetch('https://main.victriedemos.com:3017/api/customers')
      ]);

      const [policiesData, claimsData, customersData] = await Promise.all([
        policiesRes.json(),
        claimsRes.json(),
        customersRes.json()
      ]);

      let message = '📊 ZAKIM PORTAL OVERVIEW\n\n';

      if (policiesData.success) {
        const policies = policiesData.data;
        const active = policies.filter(p => p.Status === 4).length;
        const pending = policies.filter(p => p.Status === 2 || p.Status === 3).length;
        
        message += `🛡️ Policies:\n`;
        message += `   • Total: ${policies.length}\n`;
        message += `   • Active: ${active}\n`;
        message += `   • Pending: ${pending}\n\n`;
      }

      if (claimsData.success) {
        const claims = claimsData.data;
        const pending = claims.filter(c => c.ClaimStatus === 'Pending').length;
        
        message += `📝 Claims:\n`;
        message += `   • Total: ${claims.length}\n`;
        message += `   • Pending: ${pending}\n\n`;
      }

      if (customersData.success) {
        message += `👥 Customers: ${customersData.data.length}\n\n`;
      }

      message += `Last updated: ${new Date().toLocaleString()}`;

      return message;
    } catch (error) {
      return '❌ Unable to fetch dashboard data.';
    }
  };

  const handleHowToQuery = (query) => {
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('create') && lowerQuery.includes('policy')) {
      return `📝 To create a new policy:\n\n` +
        `1. Navigate to "Create Policy" from the sidebar\n` +
        `2. Select the product type\n` +
        `3. Fill in customer details\n` +
        `4. Enter vehicle/asset information\n` +
        `5. Calculate quote\n` +
        `6. Review and submit for approval\n\n` +
        `Need more help? Ask me specific questions!`;
    }

    if (lowerQuery.includes('approve') && lowerQuery.includes('policy')) {
      return `✅ To approve a policy:\n\n` +
        `1. Go to "New Policies" or "Pending Approval"\n` +
        `2. Click on the policy to review details\n` +
        `3. Verify all information\n` +
        `4. Click "Approve" (with or without payment)\n` +
        `5. Add any approval notes\n\n` +
        `Note: Your role (${userRole}) may affect approval permissions.`;
    }

    if (lowerQuery.includes('claim')) {
      return `📋 To process a claim:\n\n` +
        `1. Navigate to "Claims" section\n` +
        `2. Select "New Claims"\n` +
        `3. Review claim details\n` +
        `4. Update claim status (Pending → Ongoing → Settled)\n` +
        `5. Add notes and documentation\n\n` +
        `You can track claim progress in the respective sections.`;
    }

    if (lowerQuery.includes('customer') || lowerQuery.includes('client')) {
      return `👥 To manage customers:\n\n` +
        `1. Go to "Clients" section\n` +
        `2. View all registered customers\n` +
        `3. Add new customer if needed\n` +
        `4. Click on customer to view their policies\n` +
        `5. Track customer history and claims\n\n` +
        `Customer data is automatically saved when creating policies.`;
    }

    return `❓ I can help you with:\n\n` +
      `• How to create a policy\n` +
      `• How to approve a policy\n` +
      `• How to process claims\n` +
      `• How to manage customers\n` +
      `• How to generate reports\n\n` +
      `Just ask me!`;
  };

  const getStatusText = (status) => {
    const statusMap = {
      1: 'Draft',
      2: 'New',
      3: 'Pending Approval',
      4: 'Active',
      5: 'Expired',
      6: 'Rejected'
    };
    return statusMap[status] || 'Unknown';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    addMessage(
      `Chat cleared! How can I help you?`,
      'bot'
    );
  };

  return (
    <div className={`ai-assistant-container ${isOpen ? 'open' : ''}`}>
      {!isOpen && (
        <button className="ai-assistant-toggle" onClick={() => setIsOpen(true)}>
          <span className="ai-icon">🤖</span>
          <span className="ai-badge">AI</span>
        </button>
      )}

      {isOpen && (
        <div className="ai-assistant-window">
          <div className="ai-assistant-header">
            <div className="ai-header-content">
              <span className="ai-icon">🤖</span>
              <div className="ai-header-text">
                <h3>Zakim AI Assistant</h3>
                <span className="ai-status">Online</span>
              </div>
            </div>
            <div className="ai-header-actions">
              <button className="ai-clear-btn" onClick={clearChat} title="Clear chat">
                🗑️
              </button>
              <button className="ai-close-btn" onClick={() => setIsOpen(false)}>
                ✕
              </button>
            </div>
          </div>

          <div className="ai-assistant-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`ai-message ${msg.sender}`}>
                <div className="ai-message-content">
                  {msg.text.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < msg.text.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="ai-message-time">
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="ai-message bot">
                <div className="ai-message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-assistant-input">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about policies, claims, customers..."
              rows="2"
            />
            <button 
              onClick={handleSend} 
              disabled={!inputText.trim() || isTyping}
              className="ai-send-btn"
            >
              ➤
            </button>
          </div>

          <div className="ai-quick-actions">
            <button onClick={() => setInputText('Show me pending policies')}>
              📋 Pending
            </button>
            <button onClick={() => setInputText('Dashboard overview')}>
              📊 Dashboard
            </button>
            <button onClick={() => setInputText('Show new claims')}>
              📝 Claims
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
